import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import { CheckCircle2, Copy } from 'lucide-react';
import React, { useState } from 'react';
import type { CreateApiKeyResponse } from '../../types';
import { useTranslation } from 'react-i18next';
import {
  buildApiKeyCurlExamples,
  buildVoiceRealtimeClientSnippet,
  type ApiKeyCurlExample,
} from './curlExamples';
import { ApiScopeBadge, CapabilityBadge } from './ScopeBadges';

export interface OneTimeKeyModalProps {
  open: boolean;
  data: CreateApiKeyResponse | null;
  onClose: () => void;
  onCopy: (key: string) => void;
}

export const OneTimeKeyModal: React.FC<OneTimeKeyModalProps> = ({
  open,
  data,
  onClose,
  onCopy,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [copiedCurlExampleKey, setCopiedCurlExampleKey] = useState<string | null>(null);
  const examples = buildApiKeyCurlExamples(data);
  if (data?.scopes?.some((scope) => scope.scope === 'audio:agent')) {
    examples.push({
      key: 'voice-realtime-client',
      titleKey: 'apiKeys.oneTime.exampleVoiceRealtimeClient',
      title: 'Realtime voice (step 2: browser connects)',
      command: buildVoiceRealtimeClientSnippet(),
    });
  }

  const handleCopy = () => {
    if (data?.apiKey) {
      onCopy(data.apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyCurlExample = (example: ApiKeyCurlExample) => {
    onCopy(example.command);
    setCopiedCurlExampleKey(example.key);
    setTimeout(() => setCopiedCurlExampleKey(null), 2000);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableEscapeKeyDown>
      <DialogTitle className="text-amber-600 dark:text-amber-500">
        {t('apiKeys.oneTime.title')}
      </DialogTitle>
      <DialogContent>
        {/* Warning */}
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <strong>{t('apiKeys.oneTime.warningStrong')}</strong> {t('apiKeys.oneTime.warning')}
          </p>
        </div>

        {/* Raw Key */}
        <div className="rounded-lg bg-gray-100 p-4 dark:bg-slate-900">
          <div className="flex items-center justify-between gap-4">
            <code className="flex-1 break-all font-mono text-sm text-gray-800 dark:text-slate-300">
              {data?.apiKey}
            </code>
            <Button
              variant="outlined"
              startIcon={<Copy size={16} />}
              onClick={handleCopy}
              size="small"
            >
              {copied ? t('apiKeys.oneTime.copied') : t('apiKeys.oneTime.copy')}
            </Button>
          </div>
        </div>

        {/* Granted Capabilities */}
        {((data?.scopes && data.scopes.length > 0) ||
          (data?.capabilities && data.capabilities.length > 0)) && (
          <div className="mt-4">
            <p className="mb-2 text-sm font-semibold text-gray-700 dark:text-slate-300">
              {t('apiKeys.oneTime.capabilitiesGranted')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {data.scopes?.length
                ? data.scopes.map((scope) => (
                    <ApiScopeBadge
                      key={`${scope.scope}:${scope.resourceType ?? ''}:${scope.resourceId ?? ''}`}
                      scope={scope}
                    />
                  ))
                : data.capabilities.map((cap) => (
                    <CapabilityBadge key={cap.capabilityCode} scope={cap} />
                  ))}
            </div>
          </div>
        )}

        {examples.length > 0 && (
          <div className="mt-4">
            <p className="mb-1 text-sm font-semibold text-gray-700 dark:text-slate-300">
              {t('apiKeys.oneTime.curlExamplesTitle')}
            </p>
            <p className="mb-3 text-xs leading-5 text-gray-500 dark:text-slate-400">
              {t('apiKeys.oneTime.curlExamplesDescription')}
            </p>
            <div className="space-y-3">
              {examples.map((example) => (
                <div
                  key={example.key}
                  className="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700"
                >
                  <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                    <span>{t(example.titleKey, { defaultValue: example.title })}</span>
                    <Tooltip
                      title={
                        copiedCurlExampleKey === example.key
                          ? t('apiKeys.oneTime.copied')
                          : t('apiKeys.oneTime.copy')
                      }
                    >
                      <IconButton
                        aria-label={t('apiKeys.oneTime.copyCurlExample')}
                        size="small"
                        onClick={() => handleCopyCurlExample(example)}
                      >
                        {copiedCurlExampleKey === example.key ? (
                          <CheckCircle2 size={16} className="text-green-600 dark:text-green-400" />
                        ) : (
                          <Copy size={16} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </div>
                  {example.note && (
                    <p className="border-b border-gray-200 px-3 py-2 text-xs leading-5 text-gray-500 dark:border-slate-700 dark:text-slate-400">
                      {t(example.noteKey ?? '', { defaultValue: example.note })}
                    </p>
                  )}
                  <pre className="overflow-x-auto bg-gray-100 p-3 text-xs leading-5 text-gray-800 dark:bg-slate-950 dark:text-slate-200">
                    <code>{example.command}</code>
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Security tip */}
        <div className="mt-4 rounded-lg border border-blue-300 bg-blue-50 p-4 dark:border-blue-700 dark:bg-blue-900/20">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            {t('apiKeys.oneTime.securityTip')}
          </p>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('apiKeys.oneTime.saved')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
